import clsx from 'clsx';
import { DocumentViewer } from 'components/domain/Document/DocumentViewer';
import { NsKey } from 'enums/NsKey';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BorrowerProjectDocumentDto } from 'services/borrowerProjectDocumentsAPI';
import { PROJECT_DOCUMENTS } from './BorrowerProjectDocumentsSection';

interface BorrowerProjectDocumentsViewerProps {
  documents: BorrowerProjectDocumentDto[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  className?: string;
}

/**
 * Project documents (loan offer, amortization schedule) read beside the loan form.
 * Receives the already loaded list; only files with a stored file are shown.
 */
export const BorrowerProjectDocumentsViewer: FC<BorrowerProjectDocumentsViewerProps> = ({
  documents,
  collapsed,
  onToggleCollapsed,
  className
}) => {
  const { t } = useTranslation(NsKey.Borrower)

  const groups = useMemo(() => PROJECT_DOCUMENTS
    .map(config => ({
      ...config,
      files: documents.filter(d => d.fileType === config.fileType && d.storedFileId)
    }))
    .filter(group => group.files.length > 0), [documents])

  const [selectedType, setSelectedType] = useState(groups[0]?.fileType)
  const selectedGroup = groups.find(g => g.fileType === selectedType) ?? groups[0]
  const [selectedFileId, setSelectedFileId] = useState(selectedGroup?.files[0]?.storedFileId)
  const selectedFile = selectedGroup?.files.find(f => f.storedFileId === selectedFileId) ?? selectedGroup?.files[0]

  const toggleLabel = collapsed ? t('BORROWER.LABELS.SHOW_DOCUMENTS') : t('BORROWER.LABELS.HIDE_DOCUMENTS')

  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      <button
        type="button"
        className="self-start text-sm font-semibold text-primary hover:text-primary/80"
        onClick={onToggleCollapsed}
      >
        {toggleLabel}
      </button>

      {!collapsed && selectedGroup && selectedFile?.storedFileId && (
        <>
          <div className="flex gap-1 self-start rounded-lg bg-gray-100 p-1">
            {groups.map(group => (
              <button
                key={group.fileType}
                type="button"
                className={clsx(
                  'rounded-md px-3 py-1.5 text-sm font-medium',
                  group.fileType === selectedGroup.fileType
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
                onClick={() => {
                  setSelectedType(group.fileType)
                  setSelectedFileId(group.files[0]?.storedFileId)
                }}
              >
                {t(group.labelKey)} ({group.files.length})
              </button>
            ))}
          </div>

          {selectedGroup.files.length > 1 && (
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {selectedGroup.files.map(file => (
                <li key={file.storedFileId}>
                  <button
                    type="button"
                    className={clsx(
                      'text-sm',
                      file.storedFileId === selectedFile.storedFileId
                        ? 'font-semibold text-primary underline'
                        : 'text-gray-600 hover:text-gray-900'
                    )}
                    onClick={() => setSelectedFileId(file.storedFileId)}
                  >
                    {file.fileName}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex-1 min-h-0">
            <DocumentViewer
              key={selectedFile.storedFileId}
              embedded
              title={selectedFile.fileName?.replace(/\.pdf$/i, '') ?? t(selectedGroup.labelKey)}
              storedFileId={selectedFile.storedFileId}
              onClose={() => {}}
              showDefaultActions
            />
          </div>
        </>
      )}
    </div>
  )
}
